import React from 'react';
import {Link} from 'react-router-dom';
import {ExternalLink} from 'lucide-react';
import Icon from '../ui/Icon';
import {getRelatedTools} from '../../data/tools';

/**
 * Компонент ToolSidebar - боковая панель со связанными инструментами
 *
 * @param {string} currentToolId - ID текущего инструмента
 * @param {string} currentSubToolId - ID текущего подинструмента (опционально)
 * @param {object} tool - Объект текущего инструмента
 */
const ToolSidebar = ({currentToolId, currentSubToolId, tool}) => {
    if (!tool) return null;

    const colorMap = {
        finance: "indigo",
        health: "emerald",
        travel: "purple",
        web: "violet",
        home: "fuchsia",
        food: "orange",
        time: "cyan",
        generators: "violet",
        popular: "indigo"
    };

    const color = colorMap[tool.category] || "indigo";
    const relatedTools = getRelatedTools(currentToolId, 3);

    return (
        <div className="space-y-8">
            {/* Блок с подинструментами/вариантами текущего инструмента */}
            {tool.subTools && tool.subTools.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        Варианты расчета
                    </h3>

                    <ul className="space-y-2">
                        {tool.subTools.map(subTool => (
                            <li key={subTool.id}>
                                <Link
                                    to={subTool.path}
                                    className={`flex items-center py-2 px-3 rounded-md transition-colors ${
                                        currentSubToolId === subTool.id
                                            ? `bg-${color}-50 dark:bg-gray-700 text-${color}-700 dark:text-${color}-400`
                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    <div className={`mr-2.5 w-1.5 h-1.5 rounded-full ${
                                        currentSubToolId === subTool.id
                                            ? `bg-${color}-500`
                                            : 'bg-gray-400 dark:bg-gray-500'
                                    }`}/>
                                    <span>{subTool.name}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Блок со связанными инструментами */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Похожие инструменты
                </h3>

                {relatedTools.length > 0 ? (
                    <ul className="space-y-4">
                        {relatedTools.map(relatedTool => (
                            <li key={relatedTool.id}>
                                <Link
                                    to={`/tools/${relatedTool.category}/${relatedTool.id}`}
                                    className="flex items-start hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-md transition-colors"
                                >
                                    <div
                                        className={`flex-shrink-0 mr-3 mt-1 bg-${color}-50 dark:bg-gray-700 p-1.5 rounded-md`}>
                                        <Icon
                                            name={relatedTool.iconName}
                                            color={colorMap[relatedTool.category] || "indigo"}
                                            size="sm"
                                        />
                                    </div>
                                    <div>
                                        <h4 className="text-gray-900 dark:text-white font-medium text-sm">
                                            {relatedTool.name}
                                        </h4>
                                        <p className="text-gray-500 dark:text-gray-400 text-xs line-clamp-2 mt-0.5">
                                            {relatedTool.shortDesc}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Нет связанных инструментов
                    </p>
                )}
            </div>

            {/* Блок с полезными ссылками или контактами */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Поделиться
                </h3>

                <div className="flex space-x-3">
                    <a
                        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tool.name)}&url=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        aria-label="Поделиться в Twitter"
                    >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path
                                d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                        </svg>
                    </a>

                    <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        aria-label="Поделиться в Facebook"
                    >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path fillRule="evenodd"
                                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                  clipRule="evenodd"></path>
                        </svg>
                    </a>

                    <a
                        href={`mailto:?subject=${encodeURIComponent(tool.name)}&body=${encodeURIComponent(window.location.href)}`}
                        className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        aria-label="Отправить по email"
                    >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ToolSidebar;